import { Button, Tooltip } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { priceRfq } from '@/api/quotations';
import { PermissionGate } from '@/components/PermissionGate';
import { useToast } from '@/components/toast/useToast';
import type { RfqData } from '@/types/generated';


/**
 * "Price this" — the bridge from a request to a quote.
 *
 * EXTRACTED RATHER THAN COPIED. This lives in two places now: the queue, where it is a row
 * shortcut, and the request's own page, where it is the primary action. Duplicating it would
 * duplicate three decisions that are easy to get subtly different — which statuses offer it, what
 * the button is called, and where it navigates afterwards — and a queue that offers "Price" on a
 * request whose own page does not is the kind of inconsistency nobody reports and everybody
 * notices.
 *
 * Only offered while the RFQ is still open, and it says REPRICE once one exists, because a second
 * press produces a REVISION rather than replacing the first quote. Labelling both states "Price"
 * would make that surprising the first time someone discovered it.
 */
export function PriceAction({ rfq, size = 'small' }: { rfq: RfqData; size?: 'small' | 'medium' }) {
  const navigate = useNavigate();

  const toast = useToast();

  const price = useMutation({
    mutationFn: () => priceRfq(rfq.id),
    onSuccess: (quotation) => navigate(`/quotations/${quotation.id}`),
    // WAS SILENT, AND SILENCE IS THE WORST OUTCOME HERE. This button sits inside a table row with nowhere
    // to put an inline alert, so a refusal — a rate card with no matching rule, a request whose customer
    // went on hold, a decided request — made the click do nothing at all. The operator pressed it again.
    onError: (error) => toast.failed(error, 'That request could not be priced.'),
  });

  // A decided request is not repriceable, and the server refuses it — so the button is not
  // offered rather than offered and rejected.
  if (rfq.status !== 'submitted' && rfq.status !== 'quoted') {
    return null;
  }

  return (
    <PermissionGate permission="quotation:create">
      <Tooltip
        title={
          rfq.status === 'quoted'
            ? 'Creates a new revision. The existing quote is kept.'
            : 'Runs the pricing rules and opens the draft quote.'
        }
      >
        <Button
          size={size}
          variant={size === 'medium' ? 'contained' : 'text'}
          disabled={price.isPending}
          onClick={() => price.mutate()}
        >
          {rfq.status === 'quoted' ? 'Reprice' : 'Price'}
        </Button>
      </Tooltip>
    </PermissionGate>
  );
}
